<template>
  <div ref="arrowBtn" class="split-button">
    <!-- Главная кнопка — первый ребёнок слота -->
    <component :is="mainChild" />

    <!-- Стрелка-открывашка дропдауна — только если есть дополнительные опции -->
    <VButton
        v-if="hasDropdownOptions"
        class="dropdown-arrow"
        :theme="theme"
        :class="{ open: dropdownOpen }"
        type="button"
        @click="toggleDropdown"
    >
      <VIcon material="expand_more" />
    </VButton>

    <!-- Дропдаун с опциями (все, кроме первой) -->
    <VPopup
        v-if="dropdownOpen"
        :target="arrowRef"
        :visible="dropdownOpen"
        position="bottom"
        inherit-width
        align="right"
        @close="closeDropdown"
        :offset="5"
    >
      <ul class="split-button-options" @click="closeDropdown">
        <li v-for="(child, index) in dropdownChildren" :key="index">
          <component :is="child" />
        </li>
      </ul>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VSplitButton.scss"/>

<script lang="ts">

import VIcon  from '@/lib/components/VIcon/VIcon.vue';
import VPopup from '@/lib/components/VPopup/VPopup.vue';
import Constants from "@/core/misc/Constants";

/**
 * Кнопка с дропдауном для дополнительных опций (split button).
 *
 * В слот передаётся список кнопок:
 *   - первая — главная, видна всегда как обычная кнопка
 *   - остальные — открываются в дропдауне по клику на стрелку
 *
 * Если кнопка в слоте только одна — стрелка не рисуется, компонент
 * ведёт себя как обычная кнопка.
 *
 * Каждая кнопка сама держит свой @click, theme, disabled. Компонент
 * не вмешивается в их поведение.
 *
 * Пример:
 *   <VSplitButton theme="primary">
 *     <VButton theme="primary" @click="save">Сохранить</VButton>
 *     <VButton @click="saveAs">Сохранить как</VButton>
 *     <VButton @click="exportFile">Экспортировать</VButton>
 *   </VSplitButton>
 */
export default defineComponent({
  name: 'VSplitButton',

  components: { VIcon, VPopup },

  props: {
    /**
     * Тема. Влияет на стиль стрелки-открывашки.
     * Кнопки в слоте сами держат свои темы.
     */
    theme: {
      type:      String,
      default:   'default',
      validator: (value: string) => Constants.BUTTON_THEMES.includes(value),
    },
  },

  data() {
    return {
      dropdownOpen: false as boolean,
      arrowRef:     null as HTMLElement | null,
    };
  },

  computed: {
    /**
     * Все валидные дети слота. Отфильтровываем текстовые ноды и комментарии —
     * нужны только реальные компоненты-кнопки.
     */
    slotChildren(): VNode[] {
      const all = this.$slots.default ? this.$slots.default() : [];
      return all.filter((node) =>
          typeof node.type === 'object' || typeof node.type === 'function'
      );
    },

    /**
     * Главная кнопка — первый элемент слота.
     */
    mainChild(): VNode | null {
      return this.slotChildren[0] ?? null;
    },

    /**
     * Опции для дропдауна — все, кроме первой.
     */
    dropdownChildren(): VNode[] {
      return this.slotChildren.slice(1);
    },

    /**
     * Есть ли дополнительные опции (т.е. больше одной кнопки в слоте).
     */
    hasDropdownOptions(): boolean {
      return this.dropdownChildren.length > 0;
    },
  },

  mounted() {
    // Валидация: в слот должна быть передана хотя бы одна кнопка
    if (this.slotChildren.length === 0) {
      throw new Error(
          '[VSplitButton] В слот должна быть передана хотя бы одна кнопка.\n' +
          'Пример использования:\n' +
          '  <VSplitButton theme="primary">\n' +
          '    <VButton theme="primary" @click="save">Сохранить</VButton>\n' +
          '    <VButton @click="saveAs">Сохранить как</VButton>\n' +
          '  </VSplitButton>'
      );
    }
    this.arrowRef = this.$refs.arrowBtn as HTMLElement | null;
  },

  methods: {
    toggleDropdown(): void {
      this.dropdownOpen = !this.dropdownOpen;
    },

    closeDropdown(): void {
      this.dropdownOpen = false;
    },
  },
});
</script>
